import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  landingUrlForLog,
  logOktaDebug,
  logStsDebug,
  serializeUnknownError,
} from '../../../../core/auth/okta-debug-log';
import { resolveAgentRoleFromClaims } from '../../../../core/auth/agent-role-claim.mapper';
import { identityClaimsFromJwt } from '../../../../core/auth/jwt-claims.util';
import { AgentAccessService } from '../../../../core/services/agent-access.service';
import { AuthSessionService } from '../../../../core/services/auth-session.service';
import { OktaAuthService } from '../../../../core/services/okta-auth.service';
import { StsTokenExchangeService } from '../../../../core/services/sts-token-exchange.service';
import { TAB_EVENTS_PAGE, TAB_WAITING_PAGE } from '../../../../core/routing/browser-tab-routes';

@Component({
  selector: 'app-login-okta-callback',
  imports: [RouterLink],
  templateUrl: './login-okta-callback.html',
  styleUrl: './login-okta-callback.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginOktaCallback implements OnInit {
  private readonly okta = inject(OktaAuthService);
  private readonly sts = inject(StsTokenExchangeService);
  private readonly auth = inject(AuthSessionService);
  private readonly access = inject(AgentAccessService);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private loginInFlight = false;

  readonly errorMessage = signal<string | null>(null);
  readonly completing = signal(true);

  ngOnInit(): void {
    logOktaDebug('LoginOktaCallback.ngOnInit', landingUrlForLog());
    void this.completeOktaLogin();
  }

  private async completeOktaLogin(): Promise<void> {
    if (this.loginInFlight) {
      logOktaDebug('LoginOktaCallback.completeOktaLogin: skipped, already in flight');
      return;
    }
    this.loginInFlight = true;
    this.completing.set(true);
    this.errorMessage.set(null);

    try {
      console.info('[Okta] callback started — exchanging ID token via STS');
      logOktaDebug('LoginOktaCallback.completeOktaLogin: handleRedirectCallback');
      const oktaIdToken = await this.okta.handleRedirectCallback();
      const oktaClaims = identityClaimsFromJwt(oktaIdToken);
      logOktaDebug('LoginOktaCallback.completeOktaLogin: Okta claims (no token)', {
        email: oktaClaims.email,
        displayName: oktaClaims.displayName,
        roleClaimCount: oktaClaims.roleClaims.length,
      });

      logStsDebug('LoginOktaCallback: calling exchangeOktaIdToken', {
        idTokenLength: oktaIdToken.length,
      });
      const stsResult = await firstValueFrom(this.sts.exchangeOktaIdToken(oktaIdToken));
      const stealthJwt = stsResult.accessToken;
      const stealthClaims = identityClaimsFromJwt(stealthJwt);

      const email =
        stsResult.userEmail ?? oktaClaims.email ?? stealthClaims.email ?? 'unknown@ecam.com';
      const displayName =
        stsResult.userFullName ??
        oktaClaims.displayName ??
        stealthClaims.displayName ??
        email;
      const stealthRole = resolveAgentRoleFromClaims(stealthClaims.roleClaims);
      const roleResult =
        stealthRole.status === 'ok'
          ? stealthRole
          : resolveAgentRoleFromClaims(oktaClaims.roleClaims);
      const role = roleResult.status === 'ok' ? roleResult.role : null;

      if (!role) {
        throw new Error('No valid EventQ role is assigned to this account.');
      }

      this.auth.establishSession(stealthJwt, email, {
        role,
        displayName,
        userId: stsResult.userId ?? null,
        expiresIn: stsResult.expiresIn,
      });
      console.info('[STS] session established', {
        email,
        role,
        displayName,
        expiresIn: stsResult.expiresIn,
      });
      logStsDebug('LoginOktaCallback: session established', {
        email,
        role,
        displayName,
        expiresIn: stsResult.expiresIn,
        stealthTokenLength: stealthJwt.length,
      });
      const landing = this.access.canOpenEventsDashboard() ? TAB_EVENTS_PAGE : TAB_WAITING_PAGE;
      const landingUrl = this.location.prepareExternalUrl(
        this.router.serializeUrl(this.router.createUrlTree(['/t1', landing])),
      );
      logOktaDebug('LoginOktaCallback: replacing document with landing', { landing, landingUrl });
      window.location.replace(landingUrl);
    } catch (error: unknown) {
      console.error('[Okta] login callback failed', error);
      const params = new URLSearchParams(window.location.search);
      const oktaError = params.get('error');
      const oktaDescription = params.get('error_description');
      if (oktaError || oktaDescription) {
        console.error('[Okta] redirect query', { error: oktaError, description: oktaDescription });
      }
      logOktaDebug('LoginOktaCallback.completeOktaLogin: failed', {
        error: serializeUnknownError(error),
        oktaQuery: { error: oktaError, description: oktaDescription },
      });
      this.errorMessage.set(
        error instanceof Error && error.message.includes('EventQ role')
          ? error.message
          : 'Okta login failed. Try again from the login page.',
      );
    } finally {
      this.completing.set(false);
      logOktaDebug('LoginOktaCallback.completeOktaLogin: finally', {
        hasError: Boolean(this.errorMessage()),
      });
    }
  }
}
